import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { ARCHIVE_RETENTION_DAYS, type PlatformDeveloper } from '@livinhome/shared';
import { listDevelopers, restoreDeveloper, restoreCluster } from '../../lib/platform-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

function daysLeft(archivedAt: string): number {
  const purge = new Date(archivedAt).getTime() + ARCHIVE_RETENTION_DAYS * 86400_000;
  return Math.max(0, Math.ceil((purge - Date.now()) / 86400_000));
}

export function PlatformArchivedPage() {
  const t = useT();
  const [devs, setDevs] = useState<PlatformDeveloper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true); setError(null);
    try { setDevs(await listDevelopers()); }
    catch (e) { setError(extractApiError(e).message); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const archivedDevs = useMemo(() => devs.filter((d) => d.archived_at), [devs]);
  const archivedClusters = useMemo(
    () => devs.filter((d) => !d.archived_at).flatMap((d) =>
      d.clusters.filter((cl) => cl.archived_at).map((cl) => ({ ...cl, developerName: d.name })),
    ),
    [devs],
  );

  async function onRestoreDev(id: string) {
    setBusyId(id);
    try { await restoreDeveloper(id); await refresh(); }
    catch (e) { setError(extractApiError(e).message); }
    finally { setBusyId(null); }
  }
  async function onRestoreCluster(id: string) {
    setBusyId(id);
    try { await restoreCluster(id); await refresh(); }
    catch (e) { setError(extractApiError(e).message); }
    finally { setBusyId(null); }
  }

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" fontWeight={600}>{t('plt.archivedTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('plt.archivedSubtitle', { days: ARCHIVE_RETENTION_DAYS })}
        </Typography>
      </div>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('plt.archiveDevSection')}</Typography>
              {archivedDevs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('plt.archiveNone')}</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>{t('common.colName')}</TableCell><TableCell>{t('plt.colDeletesIn')}</TableCell><TableCell align="right">{t('plt.colAction')}</TableCell></TableRow></TableHead>
                    <TableBody>
                      {archivedDevs.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.name}</TableCell>
                          <TableCell><Chip size="small" color="warning" label={t('plt.daysLeft', { days: daysLeft(d.archived_at!) })} /></TableCell>
                          <TableCell align="right">
                            <Button size="small" startIcon={<RestoreIcon />} disabled={busyId === d.id} onClick={() => onRestoreDev(d.id)}>{t('plt.restore')}</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('plt.archiveClusterSection')}</Typography>
              {archivedClusters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('plt.archiveNone')}</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>{t('plt.colCluster')}</TableCell><TableCell>{t('plt.devTitle')}</TableCell><TableCell>{t('plt.colDeletesIn')}</TableCell><TableCell align="right">{t('plt.colAction')}</TableCell></TableRow></TableHead>
                    <TableBody>
                      {archivedClusters.map((cl) => (
                        <TableRow key={cl.id} hover>
                          <TableCell>{cl.name}</TableCell>
                          <TableCell>{cl.developerName}</TableCell>
                          <TableCell><Chip size="small" color="warning" label={t('plt.daysLeft', { days: daysLeft(cl.archived_at!) })} /></TableCell>
                          <TableCell align="right">
                            <Button size="small" startIcon={<RestoreIcon />} disabled={busyId === cl.id} onClick={() => onRestoreCluster(cl.id)}>{t('plt.restore')}</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
